import { inject, injectable } from "@launchtray/tsyringe-async";
import { VariableSaver } from "by/gamefactory/tools/variableSaver/VariableSaver";
import { Renderer } from "pixi.js";
import { IAnalyticProviderWrapper } from "../../../../../analytics/IAnalyticProviderWrapper";
import { ICanvasScaler } from "../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../shared/async/PromiseCompletionSource";
import { Viewable } from "../../../../../scene/viewable/Viewable";
import { ILogger } from "../../../../logger/ILogger";
import { AsyncOperationNotification } from "../../../common/components/AsyncOperationNotification";
import { IFirstTimeExperienceController } from "../../../common/controller/IFirstTimeExperienceController";
import { IPlayerModel } from "../../../common/models/IPlayerModel";
import { IRenterPolicyModel } from "../../../common/models/Renters/IRenterPolicyModel";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { SoundsResourcePackageWrapper } from "../../../common/resources/SoundsResourcePackageWrapper";
import { TopPanel } from "../../../game/components/toppanel/TopPanel";
import { IGameController } from "../../../game/controllers/IGameController";
import { PiggyAnimationPromiseHolder } from "../../buildingUpgradeItem/components/PiggyAnimationPromiseHolder";
import { IMyBuildingsController } from "../controller/IMyBuildingsController";
import { MyBuildingsResourcePackage } from "../resources/MyBuildingsResourcePackage";
import { MyBuildingsTutorialResourcePackage } from "../resources/MyBuildingsTutorialResourcePackage";
import { UpgradeResourcePackage } from "../resources/UpgradeResourcePackage";
import { MyBuildingsPopupView } from "../view/MyBuildingsPopupView";
import { DynamicResourcesPackage } from "../../../../../resources/DynamicResourcesPackage";
import { CancellationToken } from "../../../../../shared/async/CancellationToken";
import { Web3Model } from "by/gamefactory/web3/models/Web3Model";
import { IGuestModel } from "../../../common/models/IGuestModel";
import { IFirstTimeExperienceModel } from "../../../common/models/IFirstTimeExperienceModel";

export type MyBuildingsSceneOptions = {
    isFirstShow: boolean;
    tutorialResources: MyBuildingsTutorialResourcePackage;
    buildingsResources: DynamicResourcesPackage;
    cancellationToken: CancellationToken;
}

@injectable()
export class MyBuildingsPopupScene extends Viewable<boolean> {
    public constructor(
        commonResources: CommonResourcesPackage,
        myBuildingsResourcePackage: MyBuildingsResourcePackage,
        upgradeResourcePackage: UpgradeResourcePackage,
        variableSaver: VariableSaver,
        web3Model: Web3Model,
        @inject("ICanvasScaler") canvasScaler: ICanvasScaler,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        @inject("IRenterPolicyModel") renterPolicyModel: IRenterPolicyModel,
        @inject("IFirstTimeExperienceController") firstTimeExperienceController: IFirstTimeExperienceController,
        @inject("IFirstTimeExperienceModel") firstTimeExperienceModel: IFirstTimeExperienceModel,
        @inject("IMyBuildingsController") myBuildingsController: IMyBuildingsController,
        @inject("IGameController") gameController: IGameController,
        @inject("IAnalyticProviderWrapper") analyticProvider: IAnalyticProviderWrapper,
        asyncOperationNotification: AsyncOperationNotification,
        topPanel: TopPanel,
        renderer: Renderer,
        soundResources: SoundsResourcePackageWrapper,
        piggyAnimationPcs: PiggyAnimationPromiseHolder,
        @inject("MyBuildingsSceneOptions") options: MyBuildingsSceneOptions,
        @inject("ILogger") logger: ILogger,
        @inject("IGuestModel") guestModel: IGuestModel
    ) {
        super();
        const pcs = new PromiseCompletionSource<boolean>();
        this.view = new MyBuildingsPopupView(
            pcs,
            options.cancellationToken,
            commonResources,
            myBuildingsResourcePackage,
            upgradeResourcePackage,
            soundResources,
            options.buildingsResources,
            options.tutorialResources,
            playerModel,
            renterPolicyModel,
            web3Model,
            myBuildingsController,
            gameController,
            firstTimeExperienceController,
            firstTimeExperienceModel,
            analyticProvider,
            piggyAnimationPcs,
            topPanel,
            renderer,
            options.isFirstShow,
            asyncOperationNotification,
            variableSaver,
            canvasScaler,
            logger,
            guestModel
        );
        pcs.result.then((withPiggyComplete: boolean) => {
            this.operationControls.complete(withPiggyComplete);
        }, (reason: unknown) => {
            this.operationControls.error(reason);
        });
    }
}
